import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ColorTypes, dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions } from "@omegagrid/tree";
import rp, { DEFAULT_GRID_EDITOR_OPTIONS, GridEditorAdapter, GridEditorOptions, RowData, formatDate, formatDateTime, fromIsoDate, toIsoDate } from "@reporting/rp-app-host";
import { User } from '@reporting/rp-types/core';
import { Ticket, Message } from "@reporting/rp-types/helpdesk";
import { Follower, HelpdeskLists, REQUEST_PRIORITIES, REQUEST_TYPES, REQUEST_STATUSES } from "../types";
import { Sidebar } from "../components";
import { FOLLOWER_LIST_OPTIONS, followerNames, followerNodes, joinFollowers, loadFollowers, splitFollowers } from "./followerTreeAdapter";

const PAGE_SIZE = 50;

const ticketPriorityColors: Record<number, string> = {
	1: `var(--og-text-color)`,
	2: `var(--og-colortype-yellow-border-color)`,
	3: `var(--og-colortype-red-border-color)`
};

/** SA users of the Responsible column - an adapter (unlike plain content) gives the dropdown its search bar. */
class ResponsibleTreeAdapter extends TreeAdapter {

	constructor(
		private items: NodeSource[]
	) { super() }

	getTree() {
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return { search: true };
	}

}

/**
 * The follower dropdown of the requests of one company: the people to pick from differ by company (tickets/followers),
 * so the Follower column has no adapter of its own - every cell carries the adapter of its company (getRows), one
 * instance per company with the list loaded once per grid. `loaded` hands the people over for the system messages.
 */
class FollowerCellAdapter extends TreeAdapter {

	private tree: Promise<NodeSource[]>;

	constructor(
		private accounting: number,
		private loaded: (followers: Follower[]) => void
	) { super() }

	getTree() {
		return this.tree ??= loadFollowers(this.accounting).then(followers => {
			this.loaded(followers);
			return followerNodes(followers);
		});
	}

	getOptions(): Partial<TreeOptions> {
		return { ...FOLLOWER_LIST_OPTIONS };
	}

}

/**
 * Requests in a grid for everybody with the helpdesk right (the request list of rp-ms-helpdesk is filtered by it).
 * An SA edits every column; anybody else the type, the follower and the escalation - the fields the edit form gives
 * them - and sees the rest read-only.
 */
export class TicketsGridEditorAdapter extends GridEditorAdapter {

	private readonly sa = rp.userProvider.isSA();
	private _columns: ColumnSource[];
	/** loaded rows as they came from the server, by id_request - the base of the system message about the changes */
	private _originals = new Map<number, Ticket>();
	/** the follower dropdowns by company, see FollowerCellAdapter */
	private _followerAdapters = new Map<number, FollowerCellAdapter>();
	// lists are used for system messages! TODO: use adapters instead, but how to get values from keys for the system messages?
	private _lists: HelpdeskLists = {
		id_type: REQUEST_TYPES.map(item => ({ k: item.k, v: msg(item.v) })),
		status: REQUEST_STATUSES.map(item => ({ k: item.k, v: msg(item.v) })),
		priority: REQUEST_PRIORITIES.map(item => ({ k: item.k, v: msg(item.v) })),
		who_responsible: [],
		// the people of the companies the user opened a follower dropdown for, as the dropdowns load them
		who_follower: []
	};

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		const sa = this.sa;
		if (sa) {
			// saUsers is SA only - the grid of anybody else shows the login the request list returns
			const saUsers = await rp.ms('users').get<User[]>('saUsers', { order: [['LOGIN_NAME', 'asc']] });
			this._lists.who_responsible = (saUsers ?? []).map(u => ({k: u.user_name, v: u.login_name}));
		}
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			content: [{
				action: 'open',
				title: msg('Open conversation'),
				icon: 'comments',
				color: ColorTypes.Blue
			}],
			filter: false
		}, {
			data: 'id_request',
			header: msg('ID'),
			t: 'n',
			z: '0',
			e: false,
			w: 70
		}, {
			data: 'accounting',
			header: '#',
			t: 'n',
			z: '0',
			e: false,
			w: 70
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			e: false,
			w: 200
		}, {
			data: 'subject',
			header: msg('Subject'),
			t: 't',
			e: false,
			w: 200
		}, {
			data: 'priority',
			header: msg('Priority'),
			t: 's',
			content: this._lists.priority,
			e: sa,
			w: 100
		}, {
			data: 'id_type',
			header: msg('Type'),
			t: 's',
			content: this._lists.id_type,
			e: true,
			w: 105
		}, {
			data: 'status',
			header: msg('Status'),
			t: 's',
			content: this._lists.status,
			e: sa,
			w: 130
		}, sa ? {
			data: 'who_responsible',
			header: msg('Responsible'),
			t: 's',
			adapter: new ResponsibleTreeAdapter(this._lists.who_responsible as NodeSource[]),
			e: true,
			w: 120
		} : {
			data: 'who_responsible_dv',
			header: msg('Responsible'),
			t: 't',
			e: false,
			w: 120
		}, {
			data: 'date_expected',
			header: msg('Date expected'),
			t: 'd',
			z: dates.getDateFormat(), // a day: the default date format of the editor carries the time
			e: sa,
			w: 130
		}, {
			data: 'effort',
			header: msg('Effort (h)'),
			t: 'n',
			z: '#,0.00',
			e: sa,
			w: 100
		}, {
			data: 'escalation',
			header: msg('Escalation'),
			t: 'b',
			e: true,
			w: 80
		}, {
			data: 'who_follower',
			header: msg('Follower'),
			// a multi-select: the cell holds the user names, the server a list separated by semicolons (save);
			// no content, no adapter: the dropdown comes with every cell (getRows), the people differ by company
			t: 'm',
			e: true,
			w: 160
		}, {
			data: 'is_open',
			header: msg('Is open'),
			t: 'b',
			e: false,
			w: 80
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145
		}, {
			data: 'who_created_dv',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 120
		}, {
			data: 'date_change_status',
			header: msg('Last answer'),
			t: 'dt',
			e: false,
			w: 145
		}, {
			data: 'who_change_status_dv',
			header: msg('Last answer by'),
			t: 't',
			e: false,
			w: 130
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const tickets = await rp.ms('helpdesk').get<Ticket[]>(`tickets`, this.createRequestParams(filters, {
			fields: ['id_request', 'company', 'subject', 'id_type', 'status', 'is_open', 'priority', 'date_created', 'who_created_dv',
					'date_change_status', 'who_change_status_dv', 'who_responsible', 'who_responsible_dv', 'who_follower', 'who_follower_dv',
					'date_expected', 'effort', 'escalation', 'accounting'],
			order: [['id_request', 'desc'], ['status', 'asc']],
			count: PAGE_SIZE
		}));
		// pages accumulate - a row edited on the first page must still find its original after the next page loaded
		tickets.forEach(ticket => this._originals.set(ticket.id_request, {...ticket}));
		// the columns with a dropdown adapter instead of content: the label comes with the value
		return tickets.map(ticket => ({data: {
			...ticket,
			who_follower: { v: splitFollowers(ticket.who_follower), dv: ticket.who_follower_dv, adapter: this.followerAdapter(ticket.accounting) },
			...(this.sa ? { who_responsible: { v: ticket.who_responsible, dv: ticket.who_responsible_dv } } : {}),
		}}));
	}

	/** The follower dropdown of a company, shared by its rows; the people it loads join the list of the system messages. */
	private followerAdapter(accounting: number): FollowerCellAdapter {
		let adapter = this._followerAdapters.get(accounting);
		if (!adapter) {
			adapter = new FollowerCellAdapter(accounting, followers => {
				const list = this._lists.who_follower;
				for (const f of followers) {
					if (!list.some(item => item.k === f.user_name)) list.push({ k: f.user_name, v: f.login_name });
				}
			});
			this._followerAdapters.set(accounting, adapter);
		}
		return adapter;
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		const columns = await this.getColumns();
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: { enabled: true, defaultSettings: [{ c: columns.findIndex(column => column.data === 'is_open'), term: true }] },
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			editing: { dateCalendarEditor: true },
			exportTypes: ["xlsx"],
			toolbar: {
				new: { enabled: false },
				export: {
					enabled: true,
					items: [
						{key: 'export.xlsx', value: 'XLSX', icon: 'file-excel'},
					],
				}
			},
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				// colorize priority cell based on its value
				if (cell.column?.data == 'priority') {
					div.style.color = ticketPriorityColors[cell.row?.data?.priority];
				}
				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			}
		});
	}

	async save(rowData: RowData[]) {
		// only the editable columns go to the server (for a non-SA user the type, the follower and the escalation -
		// TicketService::save keeps the rest of a partial update): the whole row used to post the read-only
		// date_change_status back as well, shifted by the time zone on every save
		const columns = this._columns.filter(column => column.e && column.data);
		for (const { data: row } of rowData) {
			const accounting = row.accounting as number;
			const ticket: Record<string, unknown> = { id_request: row.id_request };
			for (const column of columns) ticket[column.data as string] = this.cellValue(column, row[column.data as string]);
			await rp.ms('helpdesk').post('tickets', ticket, { accounting });
			const sysMessage: Message = {
				id_request: row.id_request as number,
				sys_message: true,
				text: '',
			}
			const originalRow = (this._originals.get(row.id_request as number) ?? {}) as Record<string, unknown>;
			// something there: a value, a picked list; not an empty one, not false, not 0
			const has = (v: unknown) => !(v == null || v === '' || v === false || v === 0);
			for (const column of columns) {
				const key = column.data as string;
				const origVal = this.cellValue(column, originalRow[key]);
				const currVal = this.cellValue(column, row[key]);
				if (origVal != currVal) {
					if (has(currVal)) {
						let val = currVal;
						if (column.t === 'd') {
							val = formatDate(fromIsoDate(currVal as string), dates.getDateFormat());
						} else if (key === 'who_follower') {
							val = followerNames(currVal as string, this._lists.who_follower);
						} else if (this._lists[key as keyof HelpdeskLists]) {
							const listItem = this._lists[key as keyof HelpdeskLists].find(item => item.k === currVal);
							val = listItem ? listItem.v : val;
						}
						sysMessage.text += `${msg('Change')} *${column.header as string}* → **${val}**. `;
					} else if (has(origVal)) {
						sysMessage.text += `*${column.header as string}* ${msg('removed')}. `;
					}
				}
			}
			if (sysMessage.text !== '') {
				await rp.ms('helpdesk').post(`messages`, sysMessage, { accounting });
			}
		}
		// sync map access: components.get() returns a Promise (and force-creates) when the sidebar is not registered
		(rp.host.components.components.get('helpdesk') as Sidebar)?.refresh();
	}

	/**
	 * The value of a cell for the server and for the comparison with the loaded row: a date cell holds a serial date of
	 * the grid (or the string it was loaded with) - the server takes the day; a datetime cell a Date - the second; the
	 * follower cell the user names picked (or the list it was loaded with) - the server takes them separated by semicolons.
	 */
	private cellValue(column: ColumnSource, v: unknown): unknown {
		if (column.t === 'd') return toIsoDate(v as Date | string | number);
		if (column.t === 'dt') return this.toDtString(v);
		if (column.data === 'who_follower') return joinFollowers(v);
		return v ?? null;
	}

	private toDtString(v: unknown): string | null {
		if (v == null || v === '') return null;
		if (v instanceof Date) return formatDateTime(v, 'yyyy-MM-dd HH:mm:ss');
		if (typeof v === 'number') return formatDateTime(v, 'yyyy-MM-dd HH:mm:ss', true);
		if (typeof v === 'string') return formatDateTime(new Date(v), 'yyyy-MM-dd HH:mm:ss');
		return null;
	}

	onAction(action: string, cell: CellModel): void {
		switch (action) {
			case 'open':
				rp.host.open(`helpdesk-ticket-${cell.row.data.id_request}`, {title: cell.row.data.subject});
				break;
		}
	}
}
